import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import api, { errMsg } from "@/lib/api";

const ITEMS = [
  { key: "email_notifications", label: "Email notifications", desc: "Instant emails for interviews, offers, BGV and other updates" },
  { key: "weekly_digest", label: "Weekly digest", desc: "Monday summary email — job matches for candidates, pipeline stats for employers" },
  { key: "interview_reminders", label: "Interview reminders", desc: "Email reminder 24 hours before a scheduled interview" },
];

export function NotificationPrefs() {
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/notification-prefs")
      .then(({ data }) => setPrefs(data.prefs))
      .catch(() => setPrefs({ email_notifications: true, weekly_digest: true, interview_reminders: true }));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/notification-prefs", prefs);
      setPrefs(data.prefs);
      toast.success("Notification preferences saved");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border border-border/60 rounded-xl p-6" data-testid="notif-prefs-card">
      <h3 className="font-heading font-semibold text-[#0B1B3D] mb-4 flex items-center gap-2">
        <Bell className="w-4 h-4 text-[#2563EB]" /> Notification preferences
      </h3>
      {!prefs ? (
        <div className="space-y-3 animate-pulse">
          {[0, 1, 2].map((i) => <div key={i} className="h-8 bg-slate-100 rounded" />)}
        </div>
      ) : (
        <div className="space-y-4">
          {ITEMS.map((item) => (
            <div key={item.key} className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-medium text-[#0B1B3D]">{item.label}</p>
                <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug">{item.desc}</p>
              </div>
              <Switch
                checked={!!prefs[item.key]}
                onCheckedChange={(v) => setPrefs({ ...prefs, [item.key]: v })}
                data-testid={`notif-pref-${item.key}`}
              />
            </div>
          ))}
          <Button size="sm" className="rounded-full w-full bg-[#0B1B3D]" onClick={save} disabled={saving} data-testid="notif-prefs-save-btn">
            {saving ? "Saving…" : "Save Preferences"}
          </Button>
        </div>
      )}
    </div>
  );
}
